import { useMemo, useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Input from '@mui/joy/Input'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Chip from '@mui/joy/Chip'
import Typography from '@mui/joy/Typography'
import type { MasterTranscriptSegment } from '../../api/dsGmeetApi'

function fmtTs(ms: number): string {
    const total = Math.floor(ms / 1000)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    const mm = String(m).padStart(2, '0')
    const ss = String(s).padStart(2, '0')
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

export function TranscriptViewer({ segments }: { segments: MasterTranscriptSegment[] }) {
    const [query, setQuery] = useState('')
    const [speaker, setSpeaker] = useState<string>('all')

    const speakers = useMemo(() => {
        const seen = new Set<string>()
        for (const s of segments) seen.add(s.speaker)
        return Array.from(seen)
    }, [segments])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return segments.filter((s) => {
            if (speaker !== 'all' && s.speaker !== speaker) return false
            if (q && !s.text.toLowerCase().includes(q)) return false
            return true
        })
    }, [segments, query, speaker])

    return (
        <Stack gap={1.5}>
            <Stack direction="row" gap={1}>
                <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ flex: 1 }} />
                <Select value={speaker} onChange={(_, v) => setSpeaker((v as string) ?? 'all')} sx={{ minWidth: 160 }}>
                    <Option value="all">All speakers</Option>
                    {speakers.map((s) => (
                        <Option key={s} value={s}>
                            {s}
                        </Option>
                    ))}
                </Select>
            </Stack>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filtered.length === 0 ? (
                    <Typography level="body-sm" color="neutral">
                        No matching segments.
                    </Typography>
                ) : (
                    filtered.map((seg, i) => (
                        <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                            <Typography level="body-xs" sx={{ minWidth: 60, color: 'text.tertiary' }}>
                                {fmtTs(seg.start_ms)}
                            </Typography>
                            <Stack gap={0.25} sx={{ flex: 1 }}>
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    <Typography level="body-sm" fontWeight="lg">
                                        {seg.speaker}
                                    </Typography>
                                    <Chip size="sm" variant="outlined">
                                        {seg.lang}
                                    </Chip>
                                </Stack>
                                <Typography level="body-sm">{seg.text}</Typography>
                            </Stack>
                        </Stack>
                    ))
                )}
            </Box>
        </Stack>
    )
}
